import { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/button';
import { chevronDown as IconChevronDown } from '@/components/icons/chevronDown';
import { chevronUp as IconChevronUp } from '@/components/icons/chevronUp';
import ErrorWithSupport from '@/components/support/ErrorWithSupport';
import { useQuantaVaultClaim } from '@/hooks/api/quanta/useQuantaVaultClaim';

interface VaultOwnershipPanelProps {
  claim: {
    vaultTxId?: string;
    canReadVault: boolean;
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    refetch: () => unknown;
    approval: { isPending: boolean; isError: boolean; mutate: () => void };
  };
}

export function VaultOwnershipPanel({ claim }: VaultOwnershipPanelProps) {
  const t = useTranslations('quanta.vaultOwnership');
  const [expanded, setExpanded] = useState(false);
  if (!claim.vaultTxId || claim.canReadVault) return null;
  const checking = claim.isLoading || claim.isFetching;
  const pending = checking || claim.approval.isPending;
  const isOpen = expanded || pending || Boolean(claim.error) || claim.approval.isError;
  const label = checking ? t('checking') : claim.approval.isPending ? t('pending') : claim.error ? t('retry') : t('verify');
  return (
    <section
      aria-labelledby="vault-ownership-title"
      aria-busy={pending}
      className="rounded-lg border border-border-primary bg-surface-primary p-3"
    >
      <Collapsible.Root open={isOpen} onOpenChange={setExpanded}>
        <Collapsible.Trigger
          id="vault-ownership-title"
          className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-text-primary"
        >
          {t('title')}
          {isOpen ? <IconChevronUp /> : <IconChevronDown />}
        </Collapsible.Trigger>
        <Collapsible.Content>
          <p className="mt-2 text-sm leading-6 text-text-secondary">{t('signedIn')}</p>
          <p className="mt-3 text-base leading-6 text-text-primary">{t('required')}</p>
          <p className="mt-2 text-sm leading-6 text-text-secondary">{t('noTransaction')}</p>
          <div aria-live="polite" className="mt-4">
            {checking ? (
              <p role="status" className="text-sm text-text-secondary">
                {t('checking')}
              </p>
            ) : (
              Boolean(claim.error) && (
                <ErrorWithSupport as="p" role="alert" className="text-sm leading-6 text-red-100">
                  {t('unavailable')}
                </ErrorWithSupport>
              )
            )}
            {claim.approval.isError && !claim.approval.isPending && (
              <ErrorWithSupport as="p" role="alert" className="text-sm leading-6 text-red-100">
                {t('failed')}
              </ErrorWithSupport>
            )}
            {claim.approval.isPending && (
              <p role="status" className="text-sm text-text-secondary">
                {t('pending')}
              </p>
            )}
          </div>
          <Button
            variant="secondary"
            className="mt-4 w-full sm:w-fit"
            aria-label={label}
            aria-busy={pending}
            loading={pending}
            disabled={pending}
            onClick={() => (claim.error ? void claim.refetch() : claim.approval.mutate())}
          >
            {label}
          </Button>
        </Collapsible.Content>
      </Collapsible.Root>
    </section>
  );
}

export default function VaultOwnership({ userId }: { userId: string }) {
  const claim = useQuantaVaultClaim(userId);
  return <VaultOwnershipPanel claim={claim} />;
}
